import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Reveal, SectionLabel, BtnRed, BtnGhost, CtaBanner } from '../components/common';
import { images, spaceTypes } from '../mock/mock';
import { Link } from 'react-router-dom';

export default function EncuentraTuRitmo() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <img src={images.crowd2} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0d]/85 via-[#0b0b0d]/70 to-[#0b0b0d]" />
        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 pt-28 w-full">
          <Reveal><SectionLabel light>Cada espacio habla de una manera distinta</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-bold uppercase text-4xl md:text-6xl leading-[1.02] text-white max-w-3xl">
              No todos los espacios necesitan música.
              <span className="block text-[var(--mt-red)]">Necesitan la música adecuada.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-xl text-white/70 leading-relaxed">
              El ritmo aparece cuando el espacio, el público y la propuesta empiezan a hablar el mismo idioma.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnRed to="/hablemos">Cuéntanos qué espacio tienes</BtnRed>
              <BtnGhost to="/hazlo-posible">Explora las posibilidades</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32 mt-checker">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8">
          <Reveal><SectionLabel>Primero, entender el lugar</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-2xl md:text-4xl text-white leading-tight">
              Antes de pensar qué puede suceder, hay que comprender dónde va a suceder.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
              Cada espacio tiene una identidad, una forma de recibir al público, un contexto y unas posibilidades
              técnicas. Por eso no trasladamos una fórmula de un lugar a otro. Observamos. Escuchamos. Y encontramos
              la propuesta que puede pertenecer de verdad a ese espacio.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SPACE TYPES */}
      <section className="pb-10">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 space-y-5">
          {spaceTypes.map((s, i) => (
            <Reveal key={s.id}>
              <div className={`grid md:grid-cols-2 gap-0 bg-[var(--mt-bg-alt)] border border-white/5 overflow-hidden ${i % 2 ? 'md:[direction:rtl]' : ''}`}>
                <div className="relative h-[300px] md:h-auto min-h-[320px] [direction:ltr]">
                  <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.6]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-8 md:p-12 [direction:ltr]">
                  <span className="font-display uppercase tracking-[0.25em] text-xs text-[var(--mt-red)]">{s.tag}</span>
                  <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-white mt-3 leading-tight">{s.title}</h3>
                  <p className="mt-4 text-white/70 font-medium">{s.lead}</p>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed">{s.text}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-white/60 text-sm">
                        <Check size={16} className="text-[var(--mt-red)] mt-0.5 shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <Link to="/hablemos" className="mt-6 inline-flex items-center gap-2 font-display uppercase tracking-wider text-sm text-white hover:text-[var(--mt-red)] transition-colors">
                    {s.cta} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner
        label="Cada espacio empieza en un lugar distinto"
        title="No necesitas saber todavía"
        redTitle="qué actividad quieres."
        text="Cuéntanos qué espacio gestionas, quién lo visita y qué te gustaría que cambiara. A partir de ahí encontraremos el ritmo."
        cta="Hablemos de tu espacio"
        image={images.openair}
      />
    </div>
  );
}
